/* eslint-disable complexity */
import _ from 'lodash';
import { Settings } from 'luxon';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import styled from 'styled-components/macro';

import { Id, Practice, PracticeEnablementConfig, User } from 'ev-types';

import {
  useGetCurrentUserQuery,
  useGetPracticeByHandleQuery,
  useGetPracticesByIdQuery,
  useUpdateUserLastPracticeMutation,
} from 'ev-api/core';
import { ErrorPayload, SESSION_TIMEOUT_ERROR_MESSAGE } from 'ev-api/errors';
import { oldEvaultApi } from 'ev-api/evault';
import { newEvaultApi } from 'ev-api/evault/new-evault';
import { InspectorConsoleSource } from 'ev-common/Inspector/InspectorTypes';
import UnsupportedBrowser from 'ev-common/UnsupportedBrowser';
import LoadingIndicator from 'ev-components/LoadingIndicator';
import { URL_PREFIX } from 'ev-config/config';
import { useAppNavigate } from 'ev-hooks/navigate';
import { appendConsoleLine, setGlobalError } from 'ev-store/actions';
import { useAppDispatch } from 'ev-store/redux';
import { refresh } from 'ev-utils/browser';
import { getPracticeEnablementConfig } from 'ev-utils/practice';
import { selectCustomerRoleForCustomer } from 'ev-utils/user';

import { ADMIN_ROOT } from 'app-admin/paths';
import LoginPaths from 'app-login/paths';

import useBrowserCompatibilityCheck from './browserCompatibilityCheck';
import { useUpdatePracticeSearchParam } from './navigate';

/**
 * primaryUser and currentUser must be nullable in order for CommonDataProvider
 * to serve unauthenticated pages. For backwards compatibility, useCommonData,
 * usePrimaryUser, and useCurrentUser behave as though primaryUser and
 * currentUser are not nullable while useNullablePrimaryUser and
 * useNullableCurrentUser serve pages that provide both authenticated and
 * unauthenticated experiences.
 */
export type CommonDataContext = {
  primaryUser?: User;
  currentUser?: User;
  currentPractice?: Practice;
  selectUser: (id: Id) => void;
  updatePractice: (practice: Practice) => void;
  isFetchingCurrentPractice: boolean;
  isFetchingPrimaryUser: boolean;
  _valid: boolean;
};

export type CommonDataContextRequired = Required<CommonDataContext>;

const PracticeParam = 'practice';
const SelectedUserParam = 'selectedUserId';

// this should rarely be imported in other files,
// but it is needed in some specific testing cases
export const commonDataContext = createContext({} as CommonDataContext);

export const CommonDataProvider = ({
  children,
  requiresAuth = true,
}: {
  children: React.ReactNode;
  requiresAuth: boolean;
}) => {
  const {
    data: primaryUser,
    isLoading: isLoadingPrimaryUser,
    isFetching: isFetchingPrimaryUser,
  } = useGetCurrentUserQuery();
  const [selectedUserId, setSelectedUserId] = useState<Id | null>(null);
  const [currentPractice, setCurrentPractice] = useState<
    Practice | undefined
  >();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const updatePracticeSearchParam = useUpdatePracticeSearchParam();
  const practiceId = primaryUser?.attributes.primary_practice_id || '0';
  const practiceHandle = searchParams.get(PracticeParam) || '';
  const isBrowserSupported = useBrowserCompatibilityCheck(currentPractice);
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const [updateUserLastPractice] = useUpdateUserLastPracticeMutation();
  const syncedLastPracticeIdRef = useRef<Id | undefined>(undefined);
  // Inlined rather than using ev-hooks/errorToast: importing that hook here (into
  // the most widely-shared provider in the app) breaks unrelated tests that mock
  // ev-store/redux and ev-store/actions directly, for reasons not fully root-caused.
  const errorToast = useCallback(
    (message: string) => {
      dispatch(setGlobalError(message));
      dispatch(
        appendConsoleLine({
          source: InspectorConsoleSource.GlobalError,
          message,
        }),
      );
    },
    [dispatch],
  );

  const {
    data: practice,
    isLoading: isLoadingPracticeByHandle,
    isFetching: isFetchingPracticeByHandle,
    error: practiceByHandleError,
  } = useGetPracticeByHandleQuery(
    { handle: practiceHandle },
    { skip: !practiceHandle, refetchOnMountOrArgChange: true },
  );

  const {
    data: practicesArray,
    isFetching: isFetchingCurrentPractice,
    isLoading: isLoadingCurrentPractice,
  } = useGetPracticesByIdQuery(
    { ids: [practiceId] },
    {
      skip: practiceId === '0' || practiceId === currentPractice?.id,
    },
  );

  // The handle-based lookup above is unauthenticated/unscoped (it's also used for
  // pre-login branding and for handle-only flows like video/front-door, where
  // requiresAuth is false and there may be no primaryUser at all). Only pages that
  // require an authenticated session need a membership check - for those, don't
  // trust the handle lookup as `currentPractice` until we can confirm membership
  // via primaryUser, otherwise a manually-entered URL for a practice the signed-in
  // user doesn't belong to can get committed to state before the fallback below
  // has a chance to run, and it never gets corrected (see practice_handles check).
  //
  // Compare against the *resolved* practice's own handle, not the raw practiceHandle
  // URL value - practiceHandle can legitimately be a hostname (or a partial match,
  // per Practice.find_one_by_handle_or_hostname) that the backend resolves to the
  // right practice but that would never match the handle-only practice_handles list
  // verbatim, producing a false "not authorized" for a practice the user does belong
  // to. Wait for the handle lookup to settle before deciding either way.
  //
  // This only matters while `currentPractice` hasn't been resolved yet: once it's
  // set (via either branch below), a later effect echoes its handle back into the
  // `practice` search param to keep the URL in sync. Re-running this check against
  // that echoed handle would be checking the app's own state against itself, and a
  // stale/incomplete practice_handles list would then wrongly flag a user's own,
  // already-correct practice as unauthorized.
  const resolvedPracticeHandle = practice?.attributes.handle;
  const isPracticeHandlePending =
    requiresAuth &&
    !currentPractice &&
    !!practiceHandle &&
    (!primaryUser || isLoadingPracticeByHandle);
  const isPracticeHandleKnownUnauthorized =
    requiresAuth &&
    !currentPractice &&
    !!practiceHandle &&
    !!primaryUser &&
    !isLoadingPracticeByHandle &&
    !!resolvedPracticeHandle &&
    !primaryUser.attributes.practice_handles?.includes(resolvedPracticeHandle);

  const p = isPracticeHandlePending
    ? undefined
    : practiceHandle && !isPracticeHandleKnownUnauthorized
      ? practice
      : practicesArray?.[0];
  const isFetchingPractice =
    isFetchingPracticeByHandle || isFetchingCurrentPractice;

  // This works around the following bug in devise
  // https://github.com/heartcombo/devise/issues/5213
  useEffect(() => {
    const error = practiceByHandleError as ErrorPayload | undefined;
    if (error?.data.error === SESSION_TIMEOUT_ERROR_MESSAGE) {
      refresh();
    }
  }, [practiceByHandleError]);

  useEffect(() => {
    if (isPracticeHandleKnownUnauthorized) {
      errorToast(
        "You don't have access to that practice. Showing your practice instead.",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPracticeHandleKnownUnauthorized]);

  useEffect(() => {
    if (!currentPractice && p) {
      setCurrentPractice(p);
    }
  }, [currentPractice, p]);

  // Resolving a practice from the URL only updates client state, but several
  // endpoints don't take a practice_id and instead fall back to the server's own
  // notion of the user's current practice (User#primary_practice, derived from
  // last_practice_id). Left unsynced, the app renders one practice's branding and
  // practice-scoped data while those fallback endpoints return another practice's
  // data. Keep the server in step - PracticeSwitcher already performs this same
  // update when a practice is switched deliberately, and sign-in does the
  // equivalent for whichever practice was signed in through.
  //
  // Guarded by a ref because this mutation invalidates the entire query cache: only
  // sync once per resolved practice, and only when the server actually disagrees.
  useEffect(() => {
    if (!requiresAuth || !primaryUser || !currentPractice) {
      return;
    }
    if (currentPractice.id === primaryUser.attributes.primary_practice_id) {
      return;
    }
    if (syncedLastPracticeIdRef.current === currentPractice.id) {
      return;
    }

    syncedLastPracticeIdRef.current = currentPractice.id;
    void updateUserLastPractice({
      userId: primaryUser.id,
      lastPracticeId: currentPractice.id,
    });
  }, [currentPractice, primaryUser, requiresAuth, updateUserLastPractice]);

  // This handles when practice gets refetched due to RTKQuery tag invalidation
  useEffect(() => {
    if (practice && practice.id === currentPractice?.id) {
      setCurrentPractice(practice);
    }
  }, [practice, currentPractice?.id]);

  useEffect(() => {
    if (currentPractice && !isFetchingPractice) {
      const currentValue = searchParams.get(PracticeParam);
      const handle = currentPractice.attributes.handle;
      const isAdminApp = pathname.includes(ADMIN_ROOT);

      if (handle !== currentValue && !isAdminApp) {
        updatePracticeSearchParam(handle);
      }
    }
  }, [
    currentPractice,
    isFetchingPractice,
    pathname,
    searchParams,
    updatePracticeSearchParam,
  ]);

  const updatePractice = useCallback(
    (practice: Practice) => setCurrentPractice(practice),
    [],
  );

  const selectUser = useCallback(
    (id: Id) => {
      if (!primaryUser) {
        return;
      }

      searchParams.set(SelectedUserParam, String(id));
      setSearchParams(searchParams, { replace: true });

      const user = _.find(primaryUser.dependents, { id }) || null;

      if (!user) {
        searchParams.delete(SelectedUserParam);
        setSearchParams(searchParams, { replace: true });
        setSelectedUserId(null);
      } else {
        setSelectedUserId(user.id);
      }
    },
    [searchParams, setSearchParams, primaryUser],
  );

  useEffect(() => {
    if (primaryUser) {
      const selectedId = searchParams.get(SelectedUserParam);
      if (selectedId) {
        selectUser(selectedId);
      }
    }
  }, [primaryUser, searchParams, selectUser]);

  useEffect(() => {
    if (!isLoadingPrimaryUser && !primaryUser && requiresAuth) {
      navigate(`${URL_PREFIX}/login/${LoginPaths.Login}`);
    }
  }, [isLoadingPrimaryUser, navigate, primaryUser, requiresAuth]);

  // A user who belongs to more than one customer has multiple customer roles.
  // Resolve `customer_role` to the one for the current practice's customer so
  // that every downstream permission read (isAdmin, charting, scheduling, etc.)
  // reflects the practice the user is actually in. Recomputes when they switch
  // practices, so no refetch of the user is needed.
  const currentCustomerId =
    currentPractice?.attributes.ev_customer_id ??
    currentPractice?.customer?.id ??
    undefined;
  const scopedPrimaryUser = useMemo(
    () => selectCustomerRoleForCustomer(primaryUser, currentCustomerId),
    [primaryUser, currentCustomerId],
  );

  const noUserAndNotRequired =
    !requiresAuth && !isLoadingPrimaryUser && !primaryUser;

  if (
    // isLoadingCurrentPractice only needs to gate the app before we have a currentPractice to
    // render with — once we do, a background refetch (e.g. from updateUserLastPractice's
    // blanket tag invalidation on every practice switch) shouldn't remount the whole tree.
    ((!primaryUser || (isLoadingCurrentPractice && !currentPractice)) &&
      requiresAuth) ||
    (isLoadingPrimaryUser && requiresAuth) ||
    isLoadingPracticeByHandle ||
    (!currentPractice && !noUserAndNotRequired)
  ) {
    return (
      <LoadingContainer>
        <LoadingIndicator />
      </LoadingContainer>
    );
  }

  if (!isBrowserSupported) {
    return <UnsupportedBrowser practice={currentPractice} />;
  }

  const currentUser =
    _.find(scopedPrimaryUser?.dependents, { id: selectedUserId as Id }) ||
    scopedPrimaryUser;

  Settings.defaultZone =
    currentUser?.attributes.timezone ??
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <commonDataContext.Provider
      value={{
        primaryUser: scopedPrimaryUser,
        currentUser,
        currentPractice,
        selectUser,
        updatePractice,
        isFetchingCurrentPractice,
        isFetchingPrimaryUser,
        _valid: true,
      }}
    >
      {children}
    </commonDataContext.Provider>
  );
};

export function useCommonData(useNullableData: boolean = false) {
  const context = useContext(commonDataContext);
  if (!context._valid) {
    console.error(
      'Attempted to use common data context outside of a CommonDataProvider',
    );
  }

  if (
    !useNullableData &&
    (!context.primaryUser || !context.currentUser || !context.currentPractice)
  ) {
    throw new Error(
      'primaryUser, currentUser, or currentPractice is undefined - did you mean to call useNullablePrimaryUser, useNullableCurrentUser, or useNullableCurrentPractice?',
    );
  }

  return context as CommonDataContextRequired;
}

export function useNullablePrimaryUser(useNullableData: boolean = true): {
  primaryUser: User | undefined;
  fetchingPrimaryUser: boolean;
} {
  return {
    primaryUser: useCommonData(useNullableData).primaryUser,
    fetchingPrimaryUser: useCommonData(useNullableData).isFetchingPrimaryUser,
  };
}

export function usePrimaryUser(): User {
  const { primaryUser } = useNullablePrimaryUser(false);
  if (!primaryUser) {
    throw new Error(
      'primaryUser is undefined - did you mean to call useNullablePrimaryUser',
    );
  }
  return primaryUser;
}

export function useNullableCurrentUser(
  useNullableData: boolean = true,
): User | undefined {
  return useCommonData(useNullableData).currentUser;
}

export function useCurrentUser(): User {
  const currentUser = useNullableCurrentUser(false);
  if (!currentUser) {
    throw new Error(
      'currentUser is undefined - did you mean to call useNullableCurrentUser',
    );
  }
  return currentUser;
}

export function useNullableCurrentPractice(
  useNullableData: boolean = true,
): Practice | undefined {
  return useCommonData(useNullableData).currentPractice;
}

export function useCurrentPractice() {
  const currentPractice = useNullableCurrentPractice(false);
  if (!currentPractice) {
    throw new Error(
      'currentPractice is undefined - did you mean to call useNullableCurrentPractice',
    );
  }
  return currentPractice;
}

export function useEvault() {
  return useEvaultForUser({ user: useCurrentUser() });
}

export function useEvaultForUser({
  user,
  accessToken,
  skip,
}: {
  user?: User;
  accessToken?: string;
  skip?: boolean;
}) {
  const vaultId = user?.attributes.vault_vault_id || '';
  const vaultHealthDocId = user?.attributes.vault_health_doc_id || '';
  const vaultToken = accessToken || user?.attributes.vault_access_token || '';
  const healthRecordId = user?.attributes.health_record_id ?? null;

  const { useGetPatientEvaultDocumentQuery } = useEvaultEndpoint();
  const {
    data: evaultRecord,
    isLoading,
    isFetching,
  } = useGetPatientEvaultDocumentQuery(
    {
      vaultId,
      vaultHealthDocId,
      vaultToken,
      healthRecordId,
    },
    {
      skip:
        !(vaultId && vaultHealthDocId && vaultToken && healthRecordId) || skip,
    },
  );

  return {
    evaultRecord,
    vaultId,
    vaultHealthDocId,
    vaultToken,
    healthRecordId,
    isLoadingEvaultRecord: isLoading,
    isFetchingEvaultRecord: isFetching,
  };
}

export function useEvaultEndpoint() {
  const currentPractice = useNullableCurrentPractice();
  const dispatch = useAppDispatch();
  const newEvault = useMemo(() => {
    return currentPractice
      ? getPracticeEnablementConfig(
          currentPractice,
          PracticeEnablementConfig.RedirectToNewEvault,
        )
      : false;
  }, [currentPractice]);

  useEffect(() => {
    if (newEvault) {
      dispatch(oldEvaultApi.util.resetApiState());
    }
  }, [newEvault, dispatch]);

  return newEvault ? newEvaultApi : oldEvaultApi;
}

const LoadingContainer = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;
